import {
  Bars3Icon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType, ReactNode, SVGProps } from "react";

interface RepeaterItemProps {
  index: number;
  label: string;
  show?: string | boolean;
  draggable?: boolean;
  onRemove?: () => void;
  children: ReactNode;
  accent?: "purple" | "indigo" | "emerald";
}

const accentMap = {
  purple:
    "bg-purple-900 text-white ring-purple-100 dark:bg-purple-500 dark:ring-purple-900/40",
  indigo:
    "bg-indigo-600 text-white ring-indigo-100 dark:bg-indigo-500 dark:ring-indigo-900/40",
  emerald:
    "bg-emerald-600 text-white ring-emerald-100 dark:bg-emerald-500 dark:ring-emerald-900/40",
};

export const RepeaterItem = ({
  index,
  label,
  show,
  draggable,
  onRemove,
  children,
  accent = "purple",
}: RepeaterItemProps) => {
  return (
    <div className="group/item relative rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600">
      {/* Subtle accent strip on the start side (RTL: right side) */}
      <span
        aria-hidden
        className="absolute inset-y-0 end-0 w-1 bg-gradient-to-b from-purple-900/70 via-purple-500/40 to-transparent dark:from-purple-400/70 dark:via-purple-500/30"
      />

      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/60 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
        <div className="flex items-center gap-2.5">
          {draggable && !show && (
            <button
              type="button"
              className="drag-handle cursor-grab rounded-md p-1 text-gray-400 transition hover:bg-white hover:text-gray-700 active:cursor-grabbing dark:hover:bg-gray-700 dark:hover:text-gray-200"
              aria-label="إعادة الترتيب"
            >
              <Bars3Icon className="size-4" />
            </button>
          )}
          <span
            className={`flex size-7 items-center justify-center rounded-lg text-xs font-bold ring-4 ${accentMap[accent]}`}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            {label}
          </h4>
        </div>

        {!show && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 rounded-lg border border-transparent px-2 py-1 text-xs font-medium text-red-600 transition hover:border-red-200 hover:bg-red-50 dark:text-red-400 dark:hover:border-red-900 dark:hover:bg-red-950/40"
            aria-label="حذف"
          >
            <XMarkIcon className="size-4" />
            حذف
          </button>
        )}
      </div>

      <div className="p-4 md:p-5">{children}</div>
    </div>
  );
};

interface RepeaterAddButtonProps {
  onClick: () => void;
  label: string;
  hint?: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
}

export const RepeaterAddButton = ({
  onClick,
  label,
  hint,
  icon: Icon = PlusIcon,
}: RepeaterAddButtonProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group/add flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50/40 px-4 py-5 text-sm text-gray-500 transition hover:border-purple-400 hover:bg-purple-50/40 hover:text-purple-900 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400 dark:hover:border-purple-500 dark:hover:bg-purple-950/20 dark:hover:text-purple-300"
    >
      <span className="flex size-9 items-center justify-center rounded-2xl bg-white text-gray-400 ring-1 ring-gray-200 transition group-hover/add:bg-purple-900 group-hover/add:text-white group-hover/add:ring-purple-300 dark:bg-gray-800 dark:ring-gray-700 dark:group-hover/add:bg-purple-500">
        <Icon className="size-5" />
      </span>
      <span className="flex flex-col items-start leading-tight">
        <span className="font-semibold">{label}</span>
        {hint && (
          <span className="text-[11px] font-normal text-gray-400 dark:text-gray-500">
            {hint}
          </span>
        )}
      </span>
    </button>
  );
};

interface RepeaterClearAllProps {
  onClick: () => void;
  label?: string;
}

export const RepeaterClearAll = ({
  onClick,
  label = "حذف الكل",
}: RepeaterClearAllProps) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
  >
    <TrashIcon className="size-4" />
    {label}
  </button>
);

interface RepeaterEmptyProps {
  title: string;
  description?: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
}

export const RepeaterEmpty = ({
  title,
  description,
  icon: Icon,
}: RepeaterEmptyProps) => (
  <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/40 px-4 py-8 text-center dark:border-gray-700 dark:bg-gray-900/30">
    {Icon && (
      <span className="mb-3 flex size-12 items-center justify-center rounded-2xl bg-white text-gray-400 ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
        <Icon className="size-6" />
      </span>
    )}
    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
      {title}
    </p>
    {description && (
      <p className="mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">
        {description}
      </p>
    )}
  </div>
);

interface InfoCalloutProps {
  title?: string;
  children: ReactNode;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  tone?: "info" | "warning";
}

export const InfoCallout = ({
  title,
  children,
  icon: Icon,
  tone = "info",
}: InfoCalloutProps) => {
  const tones = {
    info: "border-blue-200 bg-blue-50/60 text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200",
    warning:
      "border-amber-200 bg-amber-50/60 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200",
  };
  const iconTones = {
    info: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
    warning:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
  };
  return (
    <div
      className={`mb-4 flex gap-3 rounded-2xl border px-4 py-3 ${tones[tone]}`}
      role="alert"
    >
      {Icon && (
        <span
          className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${iconTones[tone]}`}
        >
          <Icon className="size-4" />
        </span>
      )}
      <div className="flex-1 text-sm">
        {title && <p className="mb-1 font-semibold">{title}</p>}
        <div className="text-[13px] leading-relaxed opacity-90">{children}</div>
      </div>
    </div>
  );
};
